import React, { useState, useEffect, useRef } from 'react';
import db from '../lib/instant';

const TaskDetailModal = ({ isOpen, onClose, task, users = [], currentUser, onTaskUpdated }) => {
  const user = currentUser || db.useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [newComment, setNewComment] = useState('');
  const timerRef = useRef(null);
  
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    tags: '',
    assigned_to: ''
  });

  // Query task activities
  const { data: activityData } = db.useQuery(task ? {
    task_activities: {},
    users: {}
  } : {});

  const activities = activityData?.task_activities?.filter(a => a.task_id === task?.id) || [];

  useEffect(() => {
    if (task) {
      setEditForm({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        due_date: task.due_date || '',
        tags: Array.isArray(task.tags) ? task.tags.join(', ') : (task.tags || ''),
        assigned_to: task.assigned_to || ''
      });
      
      // Calculate elapsed time
      const baseTime = parseInt(task.time_spent || '0');
      if (task.is_timer_running === 'true' && task.timer_started_at) {
        const sessionTime = Math.floor((Date.now() - new Date(task.timer_started_at).getTime()) / 1000);
        setElapsedTime(baseTime + sessionTime);
      } else {
        setElapsedTime(baseTime);
      }
    }
  }, [task]);

  // Update timer display
  useEffect(() => {
    if (task?.is_timer_running === 'true') {
      timerRef.current = setInterval(() => {
        const baseTime = parseInt(task.time_spent || '0');
        if (task.timer_started_at) {
          const sessionTime = Math.floor((Date.now() - new Date(task.timer_started_at).getTime()) / 1000);
          setElapsedTime(baseTime + sessionTime);
        }
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [task?.is_timer_running, task?.timer_started_at, task?.time_spent]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const logActivity = async (action, description, oldValue = null, newValue = null) => {
    const activityId = crypto.randomUUID();
    await db.transact([
      db.tx.task_activities[activityId].update({
        task_id: task.id,
        user_id: user.id,
        action,
        description,
        old_value: oldValue,
        new_value: newValue,
        created_at: new Date().toISOString()
      })
    ]);
  };

  const handleStartTimer = async () => {
    setLoading(true);
    try {
      await db.transact([
        db.tx.tasks[task.id].update({
          ...task,
          is_timer_running: 'true',
          timer_started_at: new Date().toISOString(),
          status: task.status === 'pending' ? 'in_progress' : task.status,
          updated_at: new Date().toISOString()
        })
      ]);
      
      await logActivity('timer_started', 'Started working on task');
      onTaskUpdated?.();
    } catch (err) {
      console.error('Error starting timer:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStopTimer = async () => {
    if (!task.timer_started_at) return;
    
    setLoading(true);
    try {
      const sessionTime = Math.floor((Date.now() - new Date(task.timer_started_at).getTime()) / 1000);
      const totalTime = parseInt(task.time_spent || '0') + sessionTime;
      
      await db.transact([
        db.tx.tasks[task.id].update({
          ...task,
          is_timer_running: 'false',
          timer_started_at: null,
          time_spent: totalTime.toString(),
          updated_at: new Date().toISOString()
        })
      ]);
      
      await logActivity('timer_stopped', `Worked for ${formatTime(sessionTime)}`);
      onTaskUpdated?.();
    } catch (err) {
      console.error('Error stopping timer:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (loading) return;
    
    setLoading(true);
    try {
      const updateData = {
        ...task,
        status: newStatus,
        updated_at: new Date().toISOString()
      };

      if (newStatus === 'completed') {
        updateData.completed_at = new Date().toISOString();
        // Auto-stop timer if running
        if (task.is_timer_running === 'true' && task.timer_started_at) {
          const sessionTime = Math.floor((Date.now() - new Date(task.timer_started_at).getTime()) / 1000);
          updateData.time_spent = (parseInt(task.time_spent || '0') + sessionTime).toString();
          updateData.is_timer_running = 'false';
          updateData.timer_started_at = null;
        }
      }

      await db.transact([
        db.tx.tasks[task.id].update(updateData)
      ]);

      await logActivity('status_changed', `Status changed from ${task.status} to ${newStatus}`, task.status, newStatus);
      onTaskUpdated?.();
    } catch (err) {
      console.error('Error updating task status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    setLoading(true);
    try {
      const tagsArray = editForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
      
      const updateData = {
        ...task,
        title: editForm.title,
        description: editForm.description,
        priority: editForm.priority,
        due_date: editForm.due_date || null,
        tags: tagsArray.length > 0 ? tagsArray : null,
        assigned_to: editForm.assigned_to || null,
        updated_at: new Date().toISOString()
      };

      await db.transact([
        db.tx.tasks[task.id].update(updateData)
      ]);

      await logActivity('updated', 'Task details updated');
      setIsEditing(false);
      onTaskUpdated?.();
    } catch (err) {
      console.error('Error updating task:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await db.transact([
        db.tx.tasks[task.id].delete()
      ]);
      onTaskUpdated?.();
      onClose();
    } catch (err) {
      console.error('Error deleting task:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      await logActivity('comment_added', newComment);
      setNewComment('');
    } catch (err) {
      console.error('Error adding comment:', err);
    }
  };

  const canEdit = () => {
    return task?.created_by === user?.id || task?.assigned_to === user?.id;
  };

  const isOverdue = () => {
    if (!task?.due_date || task?.status === 'completed') return false;
    return new Date(task.due_date) < new Date();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (!isOpen || !task) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl animate-slideUp">
        {/* Header */}
        <div className={`p-6 border-b ${isOverdue() ? 'bg-red-50 border-red-200' : 'border-gray-200'}`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  className="text-2xl font-bold text-gray-900 w-full border-none outline-none bg-transparent resize-none"
                  placeholder="Task title..."
                />
              ) : (
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{task.title}</h2>
              )}
              
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 text-sm rounded-full border ${getPriorityColor(task.priority)}`}>
                  {task.priority} priority
                </span>
                <span className={`px-3 py-1 text-sm rounded-full border ${getStatusColor(task.status)}`}>
                  {task.status.replace('_', ' ')}
                </span>
                {task.group_id === null && (
                  <span className="px-3 py-1 text-sm bg-blue-50 text-blue-700 rounded-full border border-blue-200">
                    Personal Task
                  </span>
                )}
                {isOverdue() && (
                  <span className="px-3 py-1 text-sm bg-red-100 text-red-800 rounded-full border border-red-200 animate-pulse">
                    Overdue
                  </span>
                )}
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <div className="flex h-full">
          {/* Main Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* Time Tracking */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    Time Tracking
                  </h3>
                  <div className="text-2xl font-mono font-bold text-blue-600">
                    {formatTime(elapsedTime)}
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {task.is_timer_running === 'true' ? (
                    <button
                      onClick={handleStopTimer}
                      disabled={loading}
                      className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all disabled:opacity-50 animate-pulse"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 10h6v4H9z"></path>
                      </svg>
                      Stop Timer
                    </button>
                  ) : (
                    <button
                      onClick={handleStartTimer}
                      disabled={loading || task.status === 'completed'}
                      className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all disabled:opacity-50"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.586a1 1 0 01.707.293L12 11l.707-.707A1 1 0 0113.414 10H15M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      Start Timer
                    </button>
                  )}
                  
                  {elapsedTime > 0 && (
                    <div className="text-sm text-gray-600">
                      Total time tracked: {formatTime(parseInt(task.time_spent || '0'))}
                    </div>
                  )}
                </div>
              </div>

              {/* Task Details */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  {isEditing ? (
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                      rows="4"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Add a description..."
                    />
                  ) : (
                    <div className="min-h-[2rem] p-3 bg-gray-50 rounded-lg">
                      {task.description || <span className="text-gray-500 italic">No description</span>}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                    {isEditing ? (
                      <select
                        value={editForm.priority}
                        onChange={(e) => setEditForm(prev => ({ ...prev, priority: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    ) : (
                      <div className={`inline-flex px-3 py-1 rounded-full text-sm ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={editForm.due_date}
                        onChange={(e) => setEditForm(prev => ({ ...prev, due_date: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="p-2 text-sm">
                        {task.due_date ? (
                          <span className={isOverdue() ? 'text-red-600 font-medium' : 'text-gray-900'}>
                            {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-gray-500 italic">No due date</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {task.tags && task.tags.length > 0 && !isEditing && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                    <div className="flex flex-wrap gap-2">
                      {task.tags.map((tag, index) => (
                        <span key={index} className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded border border-blue-200">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {isEditing && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                    <input
                      type="text"
                      value={editForm.tags}
                      onChange={(e) => setEditForm(prev => ({ ...prev, tags: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter tags separated by commas"
                    />
                  </div>
                )}
              </div>

              {/* Status Actions */}
              {canEdit() && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Actions</h3>
                  <div className="flex flex-wrap gap-2">
                    {task.status !== 'completed' && (
                      <>
                        {task.status === 'pending' && (
                          <button
                            onClick={() => handleStatusChange('in_progress')}
                            disabled={loading}
                            className="flex items-center px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-all disabled:opacity-50"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.586a1 1 0 01.707.293L12 11l.707-.707A1 1 0 0113.414 10H15M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            Start Task
                          </button>
                        )}
                        <button
                          onClick={() => handleStatusChange('completed')}
                          disabled={loading}
                          className="flex items-center px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition-all disabled:opacity-50"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          Complete
                        </button>
                      </>
                    )}
                    
                    {task.status === 'completed' && (
                      <button
                        onClick={() => handleStatusChange('pending')}
                        disabled={loading}
                        className="flex items-center px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm transition-all disabled:opacity-50"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        Reopen
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Comments */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Comment</h3>
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                    placeholder="Add a comment..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Activity Sidebar */}
          <div className="w-80 border-l border-gray-200 bg-gray-50">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Activity</h3>
            </div>
            <div className="p-4 overflow-y-auto h-full space-y-3">
              {activities.length === 0 ? (
                <p className="text-gray-500 text-sm italic">No activity yet</p>
              ) : (
                activities
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map((activity) => {
                    const activityUser = activityData?.users?.find(u => u.id === activity.user_id);
                    return (
                      <div key={activity.id} className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex items-start space-x-2">
                          <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-900">{activity.description}</p>
                            <div className="flex items-center text-xs text-gray-500 mt-1">
                              <span>{activityUser?.email || 'Unknown user'}</span>
                              <span className="mx-1">•</span>
                              <span>{new Date(activity.created_at).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="flex space-x-2">
            {canEdit() && (
              <>
                {isEditing ? (
                  <>
                    <button
                      onClick={handleSaveEdit}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 transition-colors"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    Edit Task
                  </button>
                )}
                
                {!showDeleteConfirm ? (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  >
                    Delete
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleDelete}
                      disabled={loading}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 transition-colors"
                    >
                      Confirm Delete
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className="text-sm text-gray-500">
            Created {new Date(task.created_at).toLocaleDateString()}
            {task.completed_at && (
              <span> • Completed {new Date(task.completed_at).toLocaleDateString()}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetailModal;